import { hexToRgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

/**
 * The Life Plan book's materials: navy book cloth, gold foil, gilt page edges, a crimson silk ribbon, cream paper
 * and a dark walnut desk under a lamp. Colours are hex strings because most are used straight as fill styles.
 */
export const BOOK = {
  cloth: '#1B2B52',
  clothDeep: '#0E1A38',
  clothLight: '#34508F',
  gold: '#D9AF56',
  goldLight: '#FFE9AE',
  goldDeep: '#8A6120',
  ribbon: '#A5202E',
  ribbonLight: '#E65C62',
  ribbonDeep: '#560E17',
  wax: '#A3202B',
  waxLight: '#E0525A',
  waxDeep: '#5A0F17',
  deskLight: '#6E4527',
  deskMid: '#47290F',
  deskDark: '#1B0E06',
  lamp: '#FFD49A',
  candle: '#FFB35E',
} as const;

/** Inks: the printed type, rules and the learner's fountain pen. */
export const INK = {
  /** Printed text: a warm, slightly brown black. */
  print: '#2A241D',
  printSoft: '#5E5446',
  printFaint: 'rgba(60,48,34,0.42)',
  rule: 'rgba(70,56,40,0.26)',
  ruleStrong: 'rgba(70,56,40,0.45)',
  /** The learner's own hand: blue-black fountain-pen ink. */
  hand: '#1F2F68',
  handSoft: 'rgba(31,47,104,0.62)',
  /** The illustrator's pen (sepia), for the drawings that are printed in the book. */
  sketch: '#3B2E22',
  pencil: 'rgba(92,84,74,0.55)',
  gold: '#B98A2C',
  goldBright: '#D9AF56',
  red: '#A0352A',
} as const;

export const RGB = {
  lamp: hexToRgb(BOOK.lamp),
  candle: hexToRgb(BOOK.candle),
  paperDark: hexToRgb('#9A7C52'),
} as const;

export type PartOrder = 0 | 1 | 2 | 3;

/** One colour world per part of the plan: Orientation, I (Now), II (Vision), III (Next steps). */
export interface PartStyle {
  /** Paper and the darker paper where it curves into the gutter. */
  paper: string;
  shade: string;
  /** The part's printed accent (index tab, rules, small caps) and a deeper tone of it. */
  accent: string;
  accentDeep: string;
  /** Watercolour washes for the drawings: main, secondary, warm highlight. */
  wash: readonly [string, string, string];
}

export const PARTS: readonly [PartStyle, PartStyle, PartStyle, PartStyle] = [
  { paper: '#F6EFDC', shade: '#E3D6B6', accent: '#9C7B3A', accentDeep: '#6E5420', wash: ['#C9A24E', '#7C93B8', '#E6B07A'] },
  { paper: '#F5EBD0', shade: '#E4D3A8', accent: '#4F7E5B', accentDeep: '#33573D', wash: ['#7FAE86', '#C9A24E', '#E59C7A'] },
  { paper: '#EFEDE0', shade: '#D8D7C4', accent: '#3F6597', accentDeep: '#2A4870', wash: ['#7FA2CF', '#E2B66B', '#9DC4B6'] },
  { paper: '#F7E8C8', shade: '#E8CF9F', accent: '#A65632', accentDeep: '#7A3A1E', wash: ['#DB8A60', '#8BAE7E', '#E6C26E'] },
];

/** The index-tab colours of Parts I, II, III. */
export const PART_TAB: readonly [string, string, string] = [PARTS[1].accent, PARTS[2].accent, PARTS[3].accent];
export const PART_TAB_DEEP: readonly [string, string, string] = [PARTS[1].accentDeep, PARTS[2].accentDeep, PARTS[3].accentDeep];

/**
 * The header and progress bar sit over the dark desk above the book in every state, so the header always takes
 * light text.
 */
export const lifePlanChromeTone = (_progress: number): ChromeTone => 'light';
